import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Loader, Message, Grid, Dimmer, Icon } from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import { useAuth } from '../../context/AuthContext';
import dashboardService, {
  DashboardStatsDto,
  ContractStatusDistributionDto,
  PeriodChartDto,
  UserPerformanceDto,
  TeamPerformanceDto,
  BkoDashboardStatsDto,
  OperationalChartDto,
  FinancialChartDto,
  StateTimeDistributionDto,
  TopPerformersResponseDto,
  TopTeamsResponseDto,
  LinesDashboardDto,
  ConsolidatedDashboardDto,
} from '../../services/dashboard/dashboardService';
import MetricCard from '../../components/Dashboard/MetricCard';
import LineMetricCard from './components/LineMetricCard';
import StatusPieChart from '../../components/Dashboard/StatusPieChart';
import ConversionFunnelChart from '../../components/Dashboard/ConversionFunnelChart';
import PeriodLineChart from '../../components/Dashboard/PeriodLineChart';
import PerformanceBarChart from '../../components/Dashboard/PerformanceBarChart';
import OperationalChart from '../../components/Dashboard/OperationalChart';
import FinancialChart from '../../components/Dashboard/FinancialChart';
import StateTimeDistributionChart from '../../components/Dashboard/StateTimeDistributionChart';
import TopPerformersList from '../../components/Dashboard/TopPerformersList';
import TopTeamsList from '../../components/Dashboard/TopTeamsList';
import CampaignRankingCard from '../../components/Campaigns/CampaignRankingCard';
import CampaignSummaryCard from '../../components/Campaigns/CampaignSummaryCard';
import CampaignEstimatesDisclaimer from '../../components/Campaigns/CampaignEstimatesDisclaimer';
import campaignService from '../../services/campaigns/campaignService';
import { CampaignDashboardDto } from '../../types/campaigns.types';
import GlassmorphismTheme from '../../styles/glassmorphism';
import ContractListModal from '../../components/Dashboard/ContractListModal';

const DASHBOARD_CACHE_TTL_MS = 30 * 60 * 1000;

type DashboardCacheEntry = {
  expiresAt: number;
  data: ConsolidatedDashboardDto;
};

type DashboardLoadOptions = {
  forceRefresh?: boolean;
};

const DashboardPage: React.FC = () => {
  const { user, hasRole } = useAuth();
  
  // Definir roles primeiro, antes dos estados que dependem delas
  const isSeller = hasRole('Seller');
  const isManager = hasRole('Manager');
  const isAdmin = hasRole('Admin');
  const isTeamLeader = hasRole('TeamLeader');
  const isBko = hasRole('BKO');
  // const isValidator = hasRole('Validator'); // Disponível para uso futuro
  
  const [stats, setStats] = useState<DashboardStatsDto | null>(null);
  const [bkoStats, setBkoStats] = useState<BkoDashboardStatsDto | null>(null);
  const [statusDistribution, setStatusDistribution] = useState<ContractStatusDistributionDto[]>([]);
  const [periodChart, setPeriodChart] = useState<PeriodChartDto[]>([]);
  const [operationalChart, setOperationalChart] = useState<OperationalChartDto[]>([]);
  const [financialChart, setFinancialChart] = useState<FinancialChartDto[]>([]);
  const [stateTimeDistribution, setStateTimeDistribution] = useState<StateTimeDistributionDto[]>([]);
  const [topPerformers, setTopPerformers] = useState<TopPerformersResponseDto | null>(null);
  const [topTeams, setTopTeams] = useState<TopTeamsResponseDto | null>(null);
  // const [teamsPerformance, setTeamsPerformance] = useState<TeamPerformanceDto[]>([]); // Disponível para uso futuro
  const [aprovadoCqv72h, setAprovadoCqv72h] = useState<number>(0);
  const [aprovadosCqvFinal, setAprovadosCqvFinal] = useState<number>(0);
  const [recusadosCqv, setRecusadosCqv] = useState<number>(0);
  const [vtmeOk, setVtmeOk] = useState<number>(0);
  const [linhasAptas, setLinhasAptas] = useState<number>(0);
  const [linesDashboard, setLinesDashboard] = useState<LinesDashboardDto | null>(null);
  const [campaignDashboard, setCampaignDashboard] = useState<CampaignDashboardDto | null>(null);
  const [_campaignLoading, setCampaignLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const [showLineMetrics, setShowLineMetrics] = useState(false);
  const dashboardRequestSeqRef = useRef(0);
  const dashboardCacheRef = useRef<Map<string, DashboardCacheEntry>>(new Map());
  
  // Estados do modal de contratos
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<number[] | null>(null);
  const [selectedStatusLabel, setSelectedStatusLabel] = useState<string>('');
  const [modalMode, setModalMode] = useState<'status' | 'linhasAptas' | 'vtmeConfirmado'>('status');
  
  // Estados reais dos filtros (aplicados) - periodKey: 'hoje' | 'ontem' | 'estaSemana' | 'ultimaSemana' | 'esteMes' | 'mesPassado' | 'personalizado'
  const [selectedPeriod, setSelectedPeriod] = useState<string>('hoje');
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(() => {
    // Para TeamLeader, usar automaticamente o ID do seu time
    return (isTeamLeader && user?.teamId) ? user.teamId : null;
  });
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [useCustomPeriod, setUseCustomPeriod] = useState<boolean>(false);
  
  // Estados temporários dos filtros (antes de aplicar)
  const [tempSelectedPeriod, setTempSelectedPeriod] = useState<string>('hoje');
  const [tempSelectedTeamId, setTempSelectedTeamId] = useState<string | null>(() => {
    // Para TeamLeader, usar automaticamente o ID do seu time
    return (isTeamLeader && user?.teamId) ? user.teamId : null;
  });
  const [tempCustomStartDate, setTempCustomStartDate] = useState<string>('');
  const [tempCustomEndDate, setTempCustomEndDate] = useState<string>('');
  const [tempUseCustomPeriod, setTempUseCustomPeriod] = useState<boolean>(false);

  // Variáveis derivadas das roles
  const canFilterByTeam = isAdmin || isTeamLeader;
  const canSeeInputEasyVendas = isAdmin || isBko || isManager || isTeamLeader;
  const canSeeMetrics = isAdmin || isBko || isManager || isTeamLeader;
  // const canSeeRevenue = isAdmin || isBko; // Disponível para uso futuro
  const canSeeTopPerformers = isAdmin || isTeamLeader;

  // Retorna valor numérico aproximado em dias para componentes que ainda usam days
  const getPeriodDaysForDisplay = useCallback((periodKey: string): number => {
    const map: Record<string, number> = { hoje: 1, ontem: 1, estaSemana: 7, ultimaSemana: 7, esteMes: 30, mesPassado: 30 };
    return map[periodKey] ?? 30;
  }, []);

  // Retorna { startDate, endDate } em ISO para cada período
  const getPeriodDates = useCallback((periodKey: string, customStart?: string, customEnd?: string): { startDate: string; endDate: string } | null => {
    const now = new Date();
    const toISO = (d: Date) => d.toISOString();
    
    if (periodKey === 'personalizado' && customStart && customEnd) {
      return { startDate: new Date(customStart).toISOString(), endDate: new Date(customEnd).toISOString() };
    }
    
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
    const endOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
    
    switch (periodKey) {
      case 'hoje':
        return { startDate: toISO(startOf(now)), endDate: toISO(endOf(now)) };
      case 'ontem': {
        const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
        return { startDate: toISO(startOf(yesterday)), endDate: toISO(endOf(yesterday)) };
      }
      case 'estaSemana': {
        const dayOfWeek = now.getDay();
        const domingo = new Date(now); domingo.setDate(now.getDate() - dayOfWeek);
        return { startDate: toISO(startOf(domingo)), endDate: toISO(endOf(now)) };
      }
      case 'ultimaSemana': {
        const dayOfWeek = now.getDay();
        const domingoPassado = new Date(now); domingoPassado.setDate(now.getDate() - dayOfWeek - 7);
        const sabadoPassado = new Date(domingoPassado); sabadoPassado.setDate(domingoPassado.getDate() + 6);
        return { startDate: toISO(startOf(domingoPassado)), endDate: toISO(endOf(sabadoPassado)) };
      }
      case 'esteMes':
        return { startDate: toISO(new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)), endDate: toISO(endOf(now)) };
      case 'mesPassado': {
        const primeiroMesPassado = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
        const ultimoMesPassado = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return { startDate: toISO(primeiroMesPassado), endDate: toISO(ultimoMesPassado) };
      }
      default:
        return null;
    }
  }, []);

  const applyConsolidatedDashboardData = useCallback((consolidatedData: ConsolidatedDashboardDto) => {
    // A API ja entrega um payload consolidado; aplicar tudo no mesmo ponto evita
    // reconciliacoes divergentes entre indicadores do contrato, VTME e linhas.
    if (isBko) {
      setBkoStats(consolidatedData.bkoStats || null);
      setOperationalChart(consolidatedData.operationalChart || []);
      setFinancialChart(consolidatedData.financialChart || []);
      setAprovadoCqv72h(consolidatedData.aprovadoCqv72h || 0);
      return;
    }

    setStats(consolidatedData.stats || null);
    setStatusDistribution(consolidatedData.statusDistribution || []);
    setPeriodChart(consolidatedData.periodChart || []);
    setOperationalChart(consolidatedData.operationalChart || []);
    setFinancialChart(consolidatedData.financialChart || []);
    setStateTimeDistribution(consolidatedData.stateTimeDistribution || []);
    setTopPerformers(consolidatedData.topPerformers || null);
    setTopTeams(consolidatedData.topTeams || null);
    setAprovadosCqvFinal(consolidatedData.aprovadosCqvFinal || 0);
    setRecusadosCqv(consolidatedData.recusadosCqv || 0);
    setAprovadoCqv72h(consolidatedData.aprovadoCqv72h || 0);
    setVtmeOk(consolidatedData.vtmeOk || 0);
    setLinhasAptas(consolidatedData.linhasRenegociacaoAptas || 0);
    setLinesDashboard(consolidatedData.lines || null);
  }, [isBko]);

  // Função interna para carregar dados com parâmetros específicos
  const loadDashboardDataWithParams = useCallback(async (
    periodKey: string,
    teamId: string | null | undefined,
    useCustom: boolean,
    customStartDate: string,
    customEndDate: string,
    options?: DashboardLoadOptions
  ) => {
    const requestSeq = ++dashboardRequestSeqRef.current;
    try {
      setLoading(true);
      setError('');
      const forceRefresh = options?.forceRefresh === true;

      // Para TeamLeader, sempre usar o ID do seu time
      const teamIdParam = canFilterByTeam 
        ? (isTeamLeader && user?.teamId ? user.teamId : teamId) 
        : undefined;
      const cacheKey = JSON.stringify({
        periodKey,
        teamId: teamIdParam ?? null,
        useCustom,
        customStartDate,
        customEndDate,
        role: isBko ? 'bko' : 'default',
      });
      const canUseSessionCache = !forceRefresh;
      const cached = canUseSessionCache ? dashboardCacheRef.current.get(cacheKey) : undefined;
      if (cached && cached.expiresAt > Date.now()) {
        applyConsolidatedDashboardData(cached.data);
        return;
      }

      const periodDates = getPeriodDates(periodKey, customStartDate, customEndDate);
      let daysParam: number | undefined;
      let startDateParam: string | undefined;
      let endDateParam: string | undefined;
      
      if (periodDates) {
        startDateParam = periodDates.startDate;
        endDateParam = periodDates.endDate;
      }
      // Fallback para compatibilidade: se não houver datas, usar days (ex: para cache)
      if (!startDateParam && periodKey === 'esteMes') {
        daysParam = 30;
      }


      // UMA ÚNICA CHAMADA CONSOLIDADA
      const consolidatedData = await dashboardService.getConsolidatedDashboard(
        daysParam, 
        teamIdParam,
        startDateParam,
        endDateParam,
        { bypassCache: forceRefresh }
      );

      if (requestSeq !== dashboardRequestSeqRef.current) {
        return;
      }
      if (canUseSessionCache) {
        dashboardCacheRef.current.set(cacheKey, {
          data: consolidatedData,
          expiresAt: Date.now() + DASHBOARD_CACHE_TTL_MS,
        });
      }
      applyConsolidatedDashboardData(consolidatedData);

    } catch (err) {
      if (requestSeq !== dashboardRequestSeqRef.current) {
        return;
      }
      let errorMessage = 'Erro ao carregar dados do dashboard';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err && typeof err === 'object' && 'response' in err) {
        const errorResponse = err as { response?: { data?: { message?: string }, status?: number } };
        if (errorResponse.response?.data?.message) {
          errorMessage = errorResponse.response.data.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      if (requestSeq === dashboardRequestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [applyConsolidatedDashboardData, canFilterByTeam, getPeriodDates, isBko, isTeamLeader, user?.teamId]);

  // Função que usa os estados atuais (para uso no useEffect inicial)
  const loadDashboardData = useCallback(async () => {
    await loadDashboardDataWithParams(
      selectedPeriod,
      selectedTeamId,
      useCustomPeriod,
      customStartDate,
      customEndDate,
      { forceRefresh: false }
    );
  }, [selectedPeriod, selectedTeamId, useCustomPeriod, customStartDate, customEndDate, loadDashboardDataWithParams]);

  // Handler para abrir modal de contratos por status
  const handleCardClick = (statuses: number[] | null, statusLabel: string, mode: 'status' | 'linhasAptas' | 'vtmeConfirmado' = 'status') => {
    setSelectedStatuses(statuses);
    setSelectedStatusLabel(statusLabel);
    setModalMode(mode);
    setShowContractModal(true);
  };

  const handleCloseModal = () => {
    setShowContractModal(false);
    setSelectedStatuses(null);
    setSelectedStatusLabel('');
    setModalMode('status');
  };

  // Função para carregar dados de campanha
  const loadCampaignData = useCallback(async () => {
    // Apenas carregar se for Admin ou TeamLeader
    if (!isAdmin && !isTeamLeader) {
      return;
    }

    try {
      setCampaignLoading(true);
      const dashboard = await campaignService.getCampaignDashboard();
      setCampaignDashboard(dashboard);
    } catch (error: any) {
      // Não mostrar erro se não houver campanha ativa (é esperado)
      if (error.response?.status !== 404) {
        console.error('Erro ao carregar dados de campanha:', error);
      }
    } finally {
      setCampaignLoading(false);
    }
  }, [isAdmin, isTeamLeader]);

  // Carregar dados apenas na inicialização
  useEffect(() => {
    let isMounted = true;
    const runInitialLoad = async () => {
      // Prioriza o topo/funil do dashboard antes de cargas secundarias (campanha).
      await loadDashboardData();
      if (isMounted) {
        await loadCampaignData();
      }
    };

    runInitialLoad();

    // Sincronizar estados temporários com estados reais na inicialização
    setTempSelectedPeriod(selectedPeriod);
    setTempSelectedTeamId(selectedTeamId);
    setTempCustomStartDate(customStartDate);
    setTempCustomEndDate(customEndDate);
    setTempUseCustomPeriod(useCustomPeriod);

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Apenas na montagem inicial

  // Função para aplicar filtros
  const handleApplyFilters = async () => {
    // Limpar erros anteriores
    setValidationError('');
    setError('');
    
    // Validar período personalizado
    if (tempUseCustomPeriod) {
      if (!tempCustomStartDate || !tempCustomEndDate) {
        setValidationError('Por favor, preencha ambas as datas (inicial e final) para o período personalizado.');
        return;
      }
      
      // Validar se data final é maior que inicial
      const start = new Date(tempCustomStartDate);
      const end = new Date(tempCustomEndDate);
      if (end <= start) {
        setValidationError('A data final deve ser maior que a data inicial.');
        return;
      }
    }
    
    const nextPeriod = tempUseCustomPeriod ? 'personalizado' : tempSelectedPeriod;
    const sameFiltersAsApplied =
      nextPeriod === selectedPeriod &&
      tempSelectedTeamId === selectedTeamId &&
      tempUseCustomPeriod === useCustomPeriod &&
      tempCustomStartDate === customStartDate &&
      tempCustomEndDate === customEndDate;

    // Aplicar filtros temporários aos estados reais
    setSelectedPeriod(nextPeriod);
    setSelectedTeamId(tempSelectedTeamId);
    setCustomStartDate(tempCustomStartDate);
    setCustomEndDate(tempCustomEndDate);
    setUseCustomPeriod(tempUseCustomPeriod);
    
    // Carregar dados com os novos filtros usando os valores temporários diretamente
    // Isso garante que usamos os valores corretos mesmo antes do React atualizar os estados
    await loadDashboardDataWithParams(
      nextPeriod,
      tempSelectedTeamId,
      tempUseCustomPeriod,
      tempCustomStartDate,
      tempCustomEndDate,
      { forceRefresh: sameFiltersAsApplied }
    );
  };

  // Função para verificar se o botão Filtrar deve estar desabilitado
  const isFilterButtonDisabled = (): boolean => {
    if (loading) return true;
    if (tempUseCustomPeriod && (!tempCustomStartDate || !tempCustomEndDate)) {
      return true;
    }
    return false;
  };

  const handlePeriodChange = (periodKey: string) => {
    setTempSelectedPeriod(periodKey);
    setTempUseCustomPeriod(false);
  };

  const handleTeamChange = (teamId: string | null) => {
    setTempSelectedTeamId(teamId);
  };

  // Função para formatar o período no subtítulo
  const getPeriodSubtitle = (): string => {
    if (useCustomPeriod && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const formatDateTime = (date: Date): string => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      };
      return `${formatDateTime(start)} até ${formatDateTime(end)}`;
    }
    const labels: Record<string, string> = {
      hoje: 'Hoje',
      ontem: 'Ontem',
      estaSemana: 'Esta semana',
      ultimaSemana: 'Última semana',
      esteMes: 'Este mês',
      mesPassado: 'Mês passado',
      personalizado: 'Personalizado'
    };
    return labels[selectedPeriod] || selectedPeriod;
  };

  // Preparar dados para gráfico de pizza
  const pieChartData = statusDistribution.map(item => ({
    name: item.statusName,
    value: item.count,
    color: item.color,
    percentage: item.percentage,
  }));

  // Calcular vendas por cluster a partir dos dados de stateTimeDistribution
  const clusterSales = useMemo(() => {
    if (!stateTimeDistribution || stateTimeDistribution.length === 0) {
      return {
        PR: 0,
        SC: 0,
        SP: 0,
        'RJ/MG': 0
      };
    }

    const totals: Record<string, number> = {
      PR: 0,
      SC: 0,
      SP: 0,
      'RJ/MG': 0
    };

    stateTimeDistribution.forEach(item => {
      const cluster = item.estado.toUpperCase();
      if (cluster === 'PR' || cluster === 'SC' || cluster === 'SP') {
        totals[cluster] = (totals[cluster] || 0) + item.quantidade;
      } else if (cluster === 'RJ/MG' || cluster === 'RJ' || cluster === 'MG') {
        totals['RJ/MG'] = (totals['RJ/MG'] || 0) + item.quantidade;
      }
    });

    return totals;
  }, [stateTimeDistribution]);

  // Exibir "Confirmado VTME" no funil e no card apenas para período Hoje ou Essa semana
  const showConfirmadoVtme = selectedPeriod === 'hoje' || selectedPeriod === 'estaSemana';

  // Preparar dados para funil de conversão (incluindo cancelados)
  const funnelData = stats ? [
    { stage: 'Criados', value: stats.contractStats.contratosCriados },
    { stage: 'Assinados', value: stats.contractStats.contratosAssinados },
    { stage: 'Aprovado CQV', value: (stats.contractStats.contratosEmAnalise || 0) + (aprovadosCqvFinal || 0) },
    showConfirmadoVtme
      ? { stage: 'Confirmado VTME', value: vtmeOk }
      : { stage: 'Ativos', value: stats.contractStats.contratosAtivos },
    { stage: 'Cancelados', value: stats.contractStats.contratosCancelados },
  ] : [];

  // Preparar dados para performance (Seller ou Manager)
  const performanceData: Array<{ name: string; total: number; ativos: number; valor?: number }> = [];

  if (isSeller && stats?.userPerformance && stats.userPerformance.length > 0) {
    const sellerData = stats.userPerformance[0];
    performanceData.push({
      name: sellerData.userName,
      total: sellerData.totalContratos,
      ativos: sellerData.contratosAtivos,
      valor: sellerData.valorTotalMensal,
    });
  } else if (isManager && stats?.userPerformance) {
    stats.userPerformance.forEach((perf: UserPerformanceDto) => {
      performanceData.push({
        name: perf.userName,
        total: perf.totalContratos,
        ativos: perf.contratosAtivos,
        valor: perf.valorTotalMensal,
      });
    });
  } else if (isAdmin && stats?.teamPerformance) {
    stats.teamPerformance.forEach((team: TeamPerformanceDto) => {
      performanceData.push({
        name: team.teamName,
        total: team.totalContratos,
        ativos: team.contratosAtivos,
        valor: team.valorTotalMensal,
      });
    });
  }

  const periodOptions = [
    { key: 'hoje', text: 'Hoje', value: 'hoje' },
    { key: 'ontem', text: 'Ontem', value: 'ontem' },
    { key: 'estaSemana', text: 'Essa semana', value: 'estaSemana' },
    { key: 'ultimaSemana', text: 'Última semana', value: 'ultimaSemana' },
    { key: 'esteMes', text: 'Este mês', value: 'esteMes' },
    { key: 'mesPassado', text: 'Mês passado', value: 'mesPassado' },
  ];

  const teamButtons = stats?.teamPerformance
    ? [
        { key: 'all', text: 'Todos os Times', value: null },
        ...stats.teamPerformance.map(team => ({
          key: team.teamId,
          text: team.teamName,
          value: team.teamId,
        })),
      ]
    : [];

  if (loading && !stats) {
    return (
      <GlassPageWrapper title="Dashboard" icon="chart line">
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <Loader active size="large">Carregando dashboard...</Loader>
        </div>
      </GlassPageWrapper>
    );
  }

  if (error && !stats) {
    return (
      <GlassPageWrapper title="Dashboard" icon="chart line">
        <Message error>
          <Message.Header>Erro</Message.Header>
          <p>{error}</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title="Dashboard de Contratos" icon="chart line">
      <Dimmer.Dimmable dimmed={loading}>
        <Dimmer active={loading} inverted>
          <Loader size="large">Filtrando dados...</Loader>
        </Dimmer>
        <div style={{ marginBottom: GlassmorphismTheme.spacing.lg }}>
          <p style={{ color: GlassmorphismTheme.colors.solid.textLight }}>
            Olá, {user?.name}! Acompanhe a performance {isSeller ? 'dos seus contratos' : 'dos contratos'}.
          </p>

          {/* Mensagem de erro de validação */}
          {validationError && (
            <Message error style={{ marginBottom: GlassmorphismTheme.spacing.md }}>
              <Message.Header>Erro na Validação</Message.Header>
              <p>{validationError}</p>
            </Message>
          )}
          
          {/* Mensagem de erro do carregamento */}
          {error && !loading && (
            <Message error style={{ marginBottom: GlassmorphismTheme.spacing.md }}>
              <Message.Header>Erro ao Carregar Dados</Message.Header>
              <p>{error}</p>
            </Message>
          )}

        {/* Filtros */}
        <div style={{
          ...GlassmorphismTheme.components.glassCard,
          padding: GlassmorphismTheme.spacing.md,
          marginBottom: GlassmorphismTheme.spacing.lg,
          display: 'flex',
          gap: GlassmorphismTheme.spacing.md,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{ flex: 1, minWidth: '200px', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              color: GlassmorphismTheme.colors.solid.textLight,
              fontWeight: 500,
              height: '20px',
              lineHeight: '20px'
            }}>
              Período
            </label>
            {/* Primeira linha: Botões de período */}
            <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', alignItems: 'center', marginBottom: tempUseCustomPeriod ? '12px' : '0' }}>
              {periodOptions.map(option => (
                <Button
                  key={option.key}
                  onClick={() => handlePeriodChange(option.value as string)}
                  active={!tempUseCustomPeriod && tempSelectedPeriod === option.value}
                  style={{
                    ...(!tempUseCustomPeriod && tempSelectedPeriod === option.value
                      ? GlassmorphismTheme.components.buttonPrimary 
                      : GlassmorphismTheme.components.buttonGlass)
                  }}
                >
                  {option.text}
                </Button>
              ))}
              <Button
                onClick={() => setTempUseCustomPeriod(!tempUseCustomPeriod)}
                active={tempUseCustomPeriod}
                style={{
                  ...(tempUseCustomPeriod 
                    ? GlassmorphismTheme.components.buttonPrimary 
                    : GlassmorphismTheme.components.buttonGlass),
                  marginLeft: '4px'
                }}
              >
                {tempUseCustomPeriod ? '✓ Personalizado' : 'Personalizado'}
              </Button>
            </div>
            {/* Segunda linha: Campos de data personalizada e botão Filtrar */}
            {tempUseCustomPeriod && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="datetime-local"
                  value={tempCustomStartDate}
                  onChange={(e) => setTempCustomStartDate(e.target.value)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    color: GlassmorphismTheme.colors.solid.textLight,
                    fontSize: '0.9rem',
                    minWidth: '180px'
                  }}
                  placeholder="Data/Hora Inicial"
                />
                <span style={{ color: GlassmorphismTheme.colors.solid.textLight, margin: '0 4px' }}>até</span>
                <input
                  type="datetime-local"
                  value={tempCustomEndDate}
                  onChange={(e) => setTempCustomEndDate(e.target.value)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    color: GlassmorphismTheme.colors.solid.textLight,
                    fontSize: '0.9rem',
                    minWidth: '180px'
                  }}
                  placeholder="Data/Hora Final"
                />
                <Button
                  onClick={handleApplyFilters}
                  disabled={isFilterButtonDisabled()}
                  loading={loading}
                  icon="filter"
                  style={{
                    ...GlassmorphismTheme.components.buttonPrimary,
                    opacity: isFilterButtonDisabled() ? 0.5 : 1,
                    cursor: isFilterButtonDisabled() ? 'not-allowed' : 'pointer',
                    marginLeft: '8px'
                  }}
                >
                  Filtrar
                </Button>
              </div>
            )}
            {/* Botão Filtrar quando não está em período personalizado */}
            {!tempUseCustomPeriod && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '12px' }}>
                <Button
                  onClick={handleApplyFilters}
                  disabled={isFilterButtonDisabled()}
                  loading={loading}
                  icon="filter"
                  style={{
                    ...GlassmorphismTheme.components.buttonPrimary,
                    opacity: isFilterButtonDisabled() ? 0.5 : 1,
                    cursor: isFilterButtonDisabled() ? 'not-allowed' : 'pointer'
                  }}
                >
                  Filtrar
                </Button>
              </div>
            )}
          </div>

          {canFilterByTeam && !isTeamLeader && teamButtons.length > 0 && (
            <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px',
                color: GlassmorphismTheme.colors.solid.textLight,
                fontWeight: 500,
                height: '20px',
                lineHeight: '20px'
              }}>
                Filtrar por Time
              </label>
              <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', alignItems: 'center' }}>
                {teamButtons.map(team => (
                  <Button
                    key={team.key}
                    onClick={() => handleTeamChange(team.value)}
                    active={tempSelectedTeamId === team.value}
                    style={{
                      ...(tempSelectedTeamId === team.value 
                        ? GlassmorphismTheme.components.buttonPrimary 
                        : GlassmorphismTheme.components.buttonGlass)
                    }}
                  >
                    {team.text}
                  </Button>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Seção Principal - Funil de Vendas */}
        <div style={{ marginBottom: GlassmorphismTheme.spacing.xl }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: GlassmorphismTheme.spacing.md,
            paddingBottom: GlassmorphismTheme.spacing.sm,
            borderBottom: '2px solid rgba(255, 255, 255, 0.15)'
          }}>
            <Icon name="chart line" size="small" style={{ color: GlassmorphismTheme.colors.solid.textLight }} />
            <h3 style={{
              margin: 0,
              color: GlassmorphismTheme.colors.solid.textLight,
              fontSize: '1.1rem',
              fontWeight: 600
            }}>
              Funil de Vendas
            </h3>
          </div>
          {isBko ? (
            <Grid stackable columns={3} style={{ position: 'relative', zIndex: 1 }}>
              <Grid.Column>
                <MetricCard
                  title="CQV OK"
                  value={bkoStats?.cqvOk || 0}
                  icon="check circle"
                  color="#2ecc71"
                  tooltip="Contratos com status Em Análise (já passaram pela CQV)"
                />
              </Grid.Column>
              <Grid.Column>
                <MetricCard
                  title="Criado Easy Vendas"
                  value={bkoStats?.criadoEasyVendas || 0}
                  icon="file outline"
                  color="#3498db"
                  tooltip="Contratos processados pela Emili (role Validator) que mudaram para Em Análise"
                />
              </Grid.Column>
              <Grid.Column>
                  <MetricCard
                    title="Input Easy Vendas"
                    value={bkoStats?.inputEasyVendas || 0}
                    icon="file text"
                    color="#f39c12"
                    tooltip="Contratos processados pela Tati (role BKO) com outros status"
                  />
              </Grid.Column>
            </Grid>
          ) : (
            <>
              {/* Funil Principal: Criados → Assinados → Aprovado CQV → Ativos */}
              <Grid stackable columns={4} style={{ position: 'relative', zIndex: 1 }}>
                <Grid.Column>
                  <MetricCard
                    title="Contratos Criados"
                    value={stats?.contractStats.contratosCriados || 0}
                    icon="file outline"
                    color="#3498db"
                    tooltip="Contratos que foram criados no sistema mas ainda não foram assinados ou processados. Clique para ver detalhes."
                    clickable={true}
                    onClick={() => handleCardClick(null, 'Criados')}
                  />
                </Grid.Column>
                <Grid.Column>
                  <MetricCard
                    title="Contratos Assinados"
                    value={stats?.contractStats.contratosAssinados || 0}
                    icon="file text"
                    color="#f39c12"
                    tooltip="Contratos na esteira de assinados (Assinado, Aprovado CQV, Recusado CQV, Ativo). Clique para ver detalhes."
                    clickable={true}
                    onClick={() => handleCardClick([1], 'Assinados')}
                  />
                </Grid.Column>
                <Grid.Column>
                  <MetricCard
                    title="Aprovado CQV"
                    value={aprovadosCqvFinal || 0}
                    icon="check circle outline"
                    color="#9b59b6"
                    tooltip="Contratos aprovados pela auditoria CQV (inclui os que já confirmaram VTME). Clique para ver detalhes."
                    clickable={true}
                    onClick={() => handleCardClick([35, 39], 'Aprovado CQV')}
                  />
                </Grid.Column>
                <Grid.Column>
                  {showConfirmadoVtme ? (
                    <MetricCard
                      title="Confirmado VTME"
                      value={vtmeOk}
                      icon="check circle"
                      color="#2ecc71"
                      tooltip="Contratos com VTME confirmado. Clique para ver detalhes."
                      clickable={true}
                      onClick={() => handleCardClick(null, 'Confirmado VTME', 'vtmeConfirmado')}
                    />
                  ) : (
                    <MetricCard
                      title="Contratos Ativos"
                      value={stats?.contractStats.contratosAtivos || 0}
                      icon="check circle"
                      color="#2ecc71"
                      tooltip="Contratos aprovados e ativos no sistema, com linhas operacionais. Clique para ver detalhes."
                      clickable={true}
                      onClick={() => handleCardClick([3], 'Ativos')}
                    />
                  )}
                </Grid.Column>
              </Grid>

              {/* Métricas Secundárias do Funil */}
              <Grid stackable columns={4} style={{ marginTop: GlassmorphismTheme.spacing.md, position: 'relative', zIndex: 1 }}>
                {(selectedPeriod === 'hoje') ? (
                  <>
                    <Grid.Column>
                      <MetricCard
                        title="Contratos Aprovado CQV 72h"
                        value={aprovadoCqv72h}
                        icon="clock"
                        color="#e74c3c"
                        tooltip="Contratos aprovados pela CQV nas últimas 72 horas"
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <MetricCard
                        title="Contratos Aprovado Período"
                        value={(stats?.contractStats.contratosEmAnalise || 0) + (aprovadosCqvFinal || 0)}
                        icon="hourglass half"
                        color="#e74c3c"
                        tooltip="Contratos com status Em Análise e Aprovado CQV."
                      />
                    </Grid.Column>
                    {canSeeMetrics && (
                      <Grid.Column>
                        <MetricCard
                          title="Linhas Aptas"
                          value={linhasAptas}
                          icon="thumbs up"
                          color="#16a085"
                          tooltip="Quantidade de linhas de renegociação marcadas como aptas pela operadora. Clique para ver detalhes."
                          clickable={true}
                          onClick={() => handleCardClick(null, 'Linhas Aptas', 'linhasAptas')}
                        />
                      </Grid.Column>
                    )}
                  </>
                ) : (
                  <>
                    {canSeeMetrics && (
                      <>
                        <Grid.Column>
                          <MetricCard
                            title="Aditivos"
                            value={stats?.businessMetrics.linhasNovas || 0}
                            icon="plus circle"
                            color="#3498db"
                            tooltip="Soma de quantidades de linhas aditivas novas (sem número), em contratos aditivos cujo primeiro VTME Criado ocorreu no período. Linhas a cancelar/suspensas não entram."
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <MetricCard
                            title="Renegociações"
                            value={stats?.businessMetrics.renegociacoes || 0}
                            icon="refresh"
                            color="#f39c12"
                            tooltip="Grupos de contratos de renegociação (regra CQV) cujo primeiro VTME Criado ocorreu no período. Contratos só de suspensão não entram."
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <MetricCard
                            title="VTME OK"
                            value={vtmeOk}
                            icon="check circle outline"
                            color="#9b59b6"
                            tooltip="Contratos com VTME confirmado no período selecionado"
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <MetricCard
                            title="Linhas Aptas"
                            value={linhasAptas}
                            icon="thumbs up"
                            color="#16a085"
                            tooltip="Quantidade de linhas de renegociação marcadas como aptas pela operadora. Clique para ver detalhes."
                            clickable={true}
                            onClick={() => handleCardClick(null, 'Linhas Aptas', 'linhasAptas')}
                          />
                        </Grid.Column>
                      </>
                    )}
                  </>
                )}
                <Grid.Column>
                  <MetricCard
                    title="Contratos Cancelados"
                    value={stats?.contractStats.contratosCancelados || 0}
                    icon="times circle"
                    color="#c0392b"
                    tooltip="Contratos cancelados. Clique para ver detalhes."
                    clickable={true}
                    onClick={() => handleCardClick([5], 'Cancelados')}
                  />
                </Grid.Column>
                {canSeeMetrics && (
                  <Grid.Column>
                    <MetricCard
                      title="Taxa Conversão"
                      value={`${(stats?.businessMetrics.taxaConversao || 0).toFixed(1)}%`}
                      icon="chart line"
                      color="#1abc9c"
                      tooltip="Percentual de contratos que foram convertidos de 'Criado' para 'Ativo'. Calculado como: (Contratos Ativos / Contratos Criados) × 100."
                    />
                  </Grid.Column>
                )}
              </Grid>
            </>
          )}
        </div>

        {/* Métricas de Linhas - Logo abaixo do Funil de Contratos */}
        {!isBko && linesDashboard && (
          showLineMetrics ? (
          <div style={{ marginTop: GlassmorphismTheme.spacing.lg }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: GlassmorphismTheme.spacing.md,
              paddingBottom: GlassmorphismTheme.spacing.sm,
              borderBottom: '2px solid rgba(255, 255, 255, 0.15)'
            }}>
              <Icon name="list" size="small" style={{ color: GlassmorphismTheme.colors.solid.textLight }} />
              <h4 style={{
                margin: 0,
                color: GlassmorphismTheme.colors.solid.textLight,
                fontSize: '1rem',
                fontWeight: 600
              }}>
                Métricas de Linhas
              </h4>
            </div>
            
            {/* Funil de Linhas: Criadas → Aprovadas CQV */}
            <Grid stackable columns={2} style={{ position: 'relative', zIndex: 1 }}>
              <Grid.Column>
                <LineMetricCard
                  title="Linhas Criadas"
                  icon="list"
                  color="#3498db"
                  metric={linesDashboard.stats.linhasCriadas}
                  tooltip="Total de linhas criadas no período"
                />
              </Grid.Column>
              <Grid.Column>
                <LineMetricCard
                  title="Linhas Aprovadas CQV"
                  icon="check circle"
                  color="#2ecc71"
                  metric={linesDashboard.stats.linhasAprovadasCqv}
                  tooltip="Linhas de contratos com status Aprovado CQV ou Ativo"
                />
              </Grid.Column>
            </Grid>

            <Grid stackable columns={2} style={{ marginTop: GlassmorphismTheme.spacing.sm }}>
              <Grid.Column>
                <LineMetricCard
                  title="Linhas Suspensas"
                  icon="pause circle"
                  color="#e74c3c"
                  metric={linesDashboard.stats.linhasSuspensas}
                  tooltip="Linhas marcadas como suspensas ou excluídas"
                />
              </Grid.Column>
              <Grid.Column>
                <LineMetricCard
                  title="Linhas de Contratos Cancelados"
                  icon="cancel"
                  color="#7f8c8d"
                  metric={linesDashboard.stats.linhasContratosCancelados}
                  tooltip="Linhas pertencentes a contratos com status Cancelado"
                />
              </Grid.Column>
            </Grid>

            {/* Métricas por Tipo de Negociação - Card único com duas seções */}
            <Grid stackable columns={1} style={{ marginTop: GlassmorphismTheme.spacing.md }}>
              <Grid.Column>
                <div style={{
                  ...GlassmorphismTheme.components.glassCard,
                  padding: GlassmorphismTheme.spacing.lg,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}>
                  <h4 style={{
                    margin: `0 0 ${GlassmorphismTheme.spacing.md} 0`,
                    color: GlassmorphismTheme.colors.solid.textLight,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'center'
                  }}>
                    Linhas por Tipo de Negociação
                  </h4>
                  
                  <Grid stackable columns={2} style={{ margin: 0 }}>
                    <Grid.Column>
                      <div style={{
                        padding: GlassmorphismTheme.spacing.md,
                        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                        textAlign: 'center'
                      }}>
                        <div style={{ marginBottom: GlassmorphismTheme.spacing.sm }}>
                          <Icon
                            name="plus circle"
                            size="large"
                            style={{
                              color: '#3498db',
                              filter: 'drop-shadow(0 4px 8px rgba(176, 196, 222, 0.2))'
                            }}
                          />
                        </div>
                        <h5 style={{
                          margin: `0 0 ${GlassmorphismTheme.spacing.xs} 0`,
                          color: GlassmorphismTheme.colors.solid.textLight,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Aditivos
                        </h5>
                        <div style={{
                          fontSize: '2rem',
                          fontWeight: 700,
                          color: '#3498db',
                          marginBottom: GlassmorphismTheme.spacing.xs,
                          lineHeight: 1
                        }}>
                          {linesDashboard.stats.linhasCriadas.aditivo.toLocaleString('pt-BR')}
                        </div>
                        <p style={{
                          margin: 0,
                          color: GlassmorphismTheme.colors.solid.textLight,
                          fontSize: '0.7rem',
                          fontStyle: 'italic'
                        }}>
                          Linhas novas adicionadas
                        </p>
                      </div>
                    </Grid.Column>
                    <Grid.Column>
                      <div style={{
                        padding: GlassmorphismTheme.spacing.md,
                        textAlign: 'center'
                      }}>
                        <div style={{ marginBottom: GlassmorphismTheme.spacing.sm }}>
                          <Icon
                            name="refresh"
                            size="large"
                            style={{
                              color: '#f39c12',
                              filter: 'drop-shadow(0 4px 8px rgba(176, 196, 222, 0.2))'
                            }}
                          />
                        </div>
                        <h5 style={{
                          margin: `0 0 ${GlassmorphismTheme.spacing.xs} 0`,
                          color: GlassmorphismTheme.colors.solid.textLight,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Renegociações
                        </h5>
                        <div style={{
                          fontSize: '2rem',
                          fontWeight: 700,
                          color: '#f39c12',
                          marginBottom: GlassmorphismTheme.spacing.xs,
                          lineHeight: 1
                        }}>
                          {linesDashboard.stats.linhasCriadas.renegociacao.toLocaleString('pt-BR')}
                        </div>
                        <p style={{
                          margin: 0,
                          color: GlassmorphismTheme.colors.solid.textLight,
                          fontSize: '0.7rem',
                          fontStyle: 'italic'
                        }}>
                          Renegociações de linhas existentes
                        </p>
                      </div>
                    </Grid.Column>
                  </Grid>
                </div>
              </Grid.Column>
            </Grid>
          </div>
          ) : (
            <div style={{ marginTop: GlassmorphismTheme.spacing.lg, textAlign: 'center' }}>
              <Button
                basic
                icon="list"
                content="Exibir métricas detalhadas de linhas"
                onClick={() => setShowLineMetrics(true)}
              />
            </div>
          )
        )}

        {/* Seções de Métricas Adicionais */}
        {canSeeMetrics && !isBko && (
          <>
            {/* Seção: Métricas Operacionais */}
            <div style={{ marginTop: GlassmorphismTheme.spacing.xl }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: GlassmorphismTheme.spacing.md,
                paddingBottom: GlassmorphismTheme.spacing.sm,
                borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Icon name="cog" size="small" style={{ color: GlassmorphismTheme.colors.solid.textLight }} />
                <h4 style={{
                  margin: 0,
                  color: GlassmorphismTheme.colors.solid.textLight,
                  fontSize: '1rem',
                  fontWeight: 600
                }}>
                  Métricas Operacionais
                </h4>
              </div>
              <Grid stackable columns={4}>
                {canSeeInputEasyVendas && (
                  <Grid.Column>
                    <MetricCard
                      title="Input Easy Vendas"
                      value={stats?.contractStats.inputEasyVendas || 0}
                      icon="file text"
                      color="#9b59b6"
                      tooltip="Contratos processados pela Tati (role BKO) com status diferentes de Criado, Assinado ou Em Análise"
                    />
                  </Grid.Column>
                )}
                <Grid.Column>
                  <MetricCard
                    title="Contratos Sem Assinatura"
                    value={stats?.contractStats.contratosSemAssinatura || 0}
                    icon="file outline"
                    color="#e74c3c"
                    tooltip="Contratos criados que ainda não foram assinados (total menos Em Análise, Assinados e Ativos)."
                  />
                </Grid.Column>
                <Grid.Column>
                  <MetricCard
                    title="Sem Assinatura > 24h"
                    value={stats?.contractStats.contratosSemAssinaturaMais24h || 0}
                    icon="clock"
                    color="#c0392b"
                    tooltip="Contratos criados há mais de 24 horas que ainda estão com status 'Criado' (não foram assinados)."
                  />
                </Grid.Column>
                {(selectedPeriod === 'hoje') && (
                  <>
                    <Grid.Column>
                      <MetricCard
                        title="Aditivos"
                        value={stats?.businessMetrics.linhasNovas || 0}
                        icon="plus circle"
                        color="#3498db"
                        tooltip="Soma de quantidades de linhas aditivas novas (sem número), em contratos aditivos cujo primeiro VTME Criado ocorreu no período. Linhas a cancelar/suspensas não entram."
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <MetricCard
                        title="Renegociações"
                        value={stats?.businessMetrics.renegociacoes || 0}
                        icon="refresh"
                        color="#f39c12"
                        tooltip="Grupos de contratos de renegociação (regra CQV) cujo primeiro VTME Criado ocorreu no período. Contratos só de suspensão não entram."
                      />
                    </Grid.Column>
                  </>
                )}
                {/* Cards CQV - Visíveis para Admin e TeamLeader */}
                {(isAdmin || isTeamLeader) && (
                  <Grid.Column>
                    <MetricCard
                      title="Recusados CQV"
                      value={recusadosCqv}
                      icon="times circle"
                      color="#e74c3c"
                      tooltip="Contratos com status 'Recusado CQV' no período selecionado"
                    />
                  </Grid.Column>
                )}
              </Grid>
            </div>

            {/* Seção: Métricas Financeiras (Admin) */}
            {isAdmin && (
              <div style={{ marginTop: GlassmorphismTheme.spacing.xl }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: GlassmorphismTheme.spacing.md,
                  paddingBottom: GlassmorphismTheme.spacing.sm,
                  borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <Icon name="dollar sign" size="small" style={{ color: GlassmorphismTheme.colors.solid.textLight }} />
                  <h4 style={{
                    margin: 0,
                    color: GlassmorphismTheme.colors.solid.textLight,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}>
                    Métricas Financeiras
                  </h4>
                </div>
                <Grid stackable columns={4}>
                  <Grid.Column>
                    <MetricCard
                      title="Receita Aditivos (Criados)"
                      value={`R$ ${(stats?.businessMetrics.receitaAditivosCriados || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      icon="dollar sign"
                      color="#3498db"
                      tooltip="Receita de linhas aditivas novas ativas em contratos aditivos com primeiro VTME Criado no período (todos os status do contrato)"
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <MetricCard
                      title="Receita Aditivos (Ativos)"
                      value={`R$ ${(stats?.businessMetrics.receitaAditivosAtivos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      icon="dollar sign"
                      color="#2ecc71"
                      tooltip="Mesma base de Aditivos (Criados), restrita a contratos em status operacional/aprovado do dashboard"
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <MetricCard
                      title="Receita Renegociações (Criados)"
                      value={`R$ ${(stats?.businessMetrics.receitaRenegociacoesCriados || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      icon="dollar sign"
                      color="#f39c12"
                      tooltip="Receita de linhas de renegociação ativas em contratos de renegociação com primeiro VTME Criado no período (todos os status; inclui Reneg Troca Plano)"
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <MetricCard
                      title="Receita Renegociações (Ativos)"
                      value={`R$ ${(stats?.businessMetrics.receitaRenegociacoesAtivos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      icon="dollar sign"
                      color="#e74c3c"
                      tooltip="Mesma base de Renegociações (Criados), restrita a contratos em status operacional/aprovado do dashboard"
                    />
                  </Grid.Column>
                </Grid>
              </div>
            )}

            {/* Seção: Métricas de Campanha - Visível para Admin e TeamLeader */}
            {(isAdmin || isTeamLeader) && campaignDashboard?.campaign && (
              <div style={{ marginTop: GlassmorphismTheme.spacing.xl }}>
                <CampaignEstimatesDisclaimer />
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: GlassmorphismTheme.spacing.md,
                  paddingBottom: GlassmorphismTheme.spacing.sm,
                  borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <Icon name="bullhorn" size="small" style={{ color: GlassmorphismTheme.colors.solid.textLight }} />
                  <h4 style={{
                    margin: 0,
                    color: GlassmorphismTheme.colors.solid.textLight,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}>
                    Métricas de Campanha
                  </h4>
                </div>
                <Grid stackable columns={isAdmin ? 2 : 1} style={{ marginTop: GlassmorphismTheme.spacing.md }}>
                  {isAdmin && (
                    <Grid.Column>
                      <CampaignSummaryCard campaign={campaignDashboard.campaign} />
                    </Grid.Column>
                  )}
                  {isAdmin && campaignDashboard.generalRanking && campaignDashboard.generalRanking.length > 0 && (
                    <Grid.Column>
                      <CampaignRankingCard
                        entries={campaignDashboard.generalRanking}
                        title={`Ranking Geral - ${campaignDashboard.campaign.nome}`}
                        subtitle={`${campaignDashboard.generalRanking.length} participantes de todos os times`}
                        showTeamColumn={true}
                      />
                    </Grid.Column>
                  )}
                  {isTeamLeader && campaignDashboard.teamRanking && campaignDashboard.teamRanking.entries.length > 0 && (
                    <Grid.Column>
                      <CampaignRankingCard
                        entries={campaignDashboard.teamRanking.entries}
                        title={`Ranking do Time - ${campaignDashboard.teamRanking.campaignNome || campaignDashboard.campaign.nome}`}
                        subtitle={`${campaignDashboard.teamRanking.entries.length} vendedor${campaignDashboard.teamRanking.entries.length !== 1 ? 'es' : ''} do seu time`}
                        showTeamColumn={false}
                      />
                    </Grid.Column>
                  )}
                </Grid>
              </div>
            )}

            {/* Top 5 Times - Visível apenas para Admin */}
            {isAdmin && topTeams && (
              <Grid stackable columns={2} style={{ marginTop: GlassmorphismTheme.spacing.lg }}>
                <Grid.Column>
                  <TopTeamsList
                    teams={topTeams.top5Melhor}
                    title="Melhor Performance - Times"
                    isBest={true}
                    days={getPeriodDaysForDisplay(selectedPeriod)}
                  />
                </Grid.Column>
                <Grid.Column>
                  <TopTeamsList
                    teams={topTeams.top5Pior}
                    title="Pior Performance - Times"
                    isBest={false}
                    days={getPeriodDaysForDisplay(selectedPeriod)}
                  />
                </Grid.Column>
              </Grid>
            )}

            {/* Top 5 Performance - Visível apenas para Admin e TeamLeader */}
            {canSeeTopPerformers && topPerformers && (
              <Grid stackable columns={2} style={{ marginTop: GlassmorphismTheme.spacing.lg }}>
                <Grid.Column>
                  <TopPerformersList
                    performers={topPerformers.top5Melhor}
                    title="Top 5 Melhor Performance"
                    isBest={true}
                  />
                </Grid.Column>
                <Grid.Column>
                  <TopPerformersList
                    performers={topPerformers.top5Pior}
                    title="Top 5 Pior Performance"
                    isBest={false}
                  />
                </Grid.Column>
              </Grid>
            )}

          </>
        )}

        {/* Seção: Vendas por Região (Admin) - Logo acima do gráfico de Picos */}
        {isAdmin && stateTimeDistribution.length > 0 && (
          <div style={{ marginTop: GlassmorphismTheme.spacing.xl }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: GlassmorphismTheme.spacing.md,
              paddingBottom: GlassmorphismTheme.spacing.sm,
              borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Icon name="map marker alternate" size="small" style={{ color: GlassmorphismTheme.colors.solid.textLight }} />
              <h4 style={{
                margin: 0,
                color: GlassmorphismTheme.colors.solid.textLight,
                fontSize: '1rem',
                fontWeight: 600
              }}>
                Vendas por Região
              </h4>
            </div>
            <Grid stackable columns={4}>
              <Grid.Column>
                <MetricCard
                  title="Vendas PR"
                  value={clusterSales.PR}
                  icon="map marker alternate"
                  color="#3498db"
                  tooltip={`Total de contratos criados no cluster Paraná (PR) - ${getPeriodSubtitle()}`}
                />
              </Grid.Column>
              <Grid.Column>
                <MetricCard
                  title="Vendas SC"
                  value={clusterSales.SC}
                  icon="map marker alternate"
                  color="#2ecc71"
                  tooltip={`Total de contratos criados no cluster Santa Catarina (SC) - ${getPeriodSubtitle()}`}
                />
              </Grid.Column>
              <Grid.Column>
                <MetricCard
                  title="Vendas SP"
                  value={clusterSales.SP}
                  icon="map marker alternate"
                  color="#f39c12"
                  tooltip={`Total de contratos criados no cluster São Paulo (SP) - ${getPeriodSubtitle()}`}
                />
              </Grid.Column>
              <Grid.Column>
                <MetricCard
                  title="Vendas RJ/MG"
                  value={clusterSales['RJ/MG']}
                  icon="map marker alternate"
                  color="#e74c3c"
                  tooltip={`Total de contratos criados no cluster Rio de Janeiro/Minas Gerais (RJ/MG) - ${getPeriodSubtitle()}`}
                />
              </Grid.Column>
            </Grid>
          </div>
        )}

        {/* Gráfico de Picos por Estado e Horário - Logo abaixo dos cards de Vendas por Região */}
        {isAdmin && stateTimeDistribution.length > 0 && (
          <Grid stackable columns={1} style={{ marginTop: GlassmorphismTheme.spacing.lg }}>
            <Grid.Column>
              <StateTimeDistributionChart
                data={stateTimeDistribution}
                title="Picos por Estado e Horário"
                subtitle={`Distribuição de contratos criados por estado e horário - ${getPeriodSubtitle()}`}
              />
            </Grid.Column>
          </Grid>
        )}

        {/* Seção: Visualização de Linhas */}
        {!isBko && linesDashboard && (
          <div style={{ marginTop: GlassmorphismTheme.spacing.xl }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: GlassmorphismTheme.spacing.md,
              paddingBottom: GlassmorphismTheme.spacing.sm,
              borderBottom: '2px solid rgba(255, 255, 255, 0.15)'
            }}>
              <Icon name="list" size="small" style={{ color: GlassmorphismTheme.colors.solid.textLight }} />
              <h3 style={{
                margin: 0,
                color: GlassmorphismTheme.colors.solid.textLight,
                fontSize: '1.1rem',
                fontWeight: 600
              }}>
                Visão de Linhas
              </h3>
            </div>

            {/* Cards principais — 2 colunas para dar espaço ao conteúdo interno */}
            <Grid stackable columns={2}>
              <Grid.Column>
                <LineMetricCard
                  title="Total de Linhas Criadas"
                  icon="list"
                  color="#3498db"
                  metric={linesDashboard.stats.linhasCriadas}
                  tooltip="Total de linhas criadas no período"
                />
              </Grid.Column>
              <Grid.Column>
                <LineMetricCard
                  title="Linhas Aprovadas CQV"
                  icon="check circle"
                  color="#2ecc71"
                  metric={linesDashboard.stats.linhasAprovadasCqv}
                  tooltip="Linhas de contratos com status Aprovado CQV ou Ativo"
                />
              </Grid.Column>
            </Grid>

            <Grid stackable columns={2} style={{ marginTop: GlassmorphismTheme.spacing.sm }}>
              <Grid.Column>
                <LineMetricCard
                  title="Linhas Suspensas"
                  icon="pause circle"
                  color="#e74c3c"
                  metric={linesDashboard.stats.linhasSuspensas}
                  tooltip="Linhas marcadas como suspensas ou excluídas"
                />
              </Grid.Column>
              <Grid.Column>
                <LineMetricCard
                  title="Linhas de Contratos Cancelados"
                  icon="cancel"
                  color="#7f8c8d"
                  metric={linesDashboard.stats.linhasContratosCancelados}
                  tooltip="Linhas pertencentes a contratos com status Cancelado"
                />
              </Grid.Column>
            </Grid>

            {/* Card de Valor Total Mensal — largura total */}
            <Grid stackable columns={1} style={{ marginTop: GlassmorphismTheme.spacing.sm }}>
              <Grid.Column>
                <MetricCard
                  title="Valor Total Mensal"
                  value={linesDashboard.stats.valorTotalMensal > 0
                    ? `R$ ${linesDashboard.stats.valorTotalMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'
                  }
                  icon="dollar sign"
                  color="#9b59b6"
                  tooltip="Valor total mensal de todas as linhas"
                />
              </Grid.Column>
            </Grid>

            {/* Gráficos de Distribuição */}
            <Grid stackable columns={2} style={{ marginTop: GlassmorphismTheme.spacing.lg }}>
              <Grid.Column>
                <StatusPieChart
                  data={linesDashboard.statusDistribution.map(item => ({
                    name: item.status,
                    value: item.count,
                    color: item.color,
                    percentage: item.percentage
                  }))}
                  title="Distribuição de Status das Linhas"
                  subtitle={`Total: ${linesDashboard.stats.linhasCriadas.total} linhas`}
                />
              </Grid.Column>
              <Grid.Column>
                <StatusPieChart
                  data={linesDashboard.negotiationDistribution.map(item => ({
                    name: item.negociacao,
                    value: item.count,
                    color: item.color,
                    percentage: item.percentage
                  }))}
                  title="Distribuição por Tipo de Negociação"
                  subtitle={`Total: ${linesDashboard.stats.linhasCriadas.total} linhas`}
                />
              </Grid.Column>
            </Grid>

            {/* Gráfico Temporal de Linhas */}
            <Grid stackable columns={1} style={{ marginTop: GlassmorphismTheme.spacing.md }}>
              <Grid.Column>
                <PeriodLineChart
                  data={linesDashboard.periodChart.map(item => ({
                    data: item.data,
                    contratosCriados: item.linhasCriadas,
                    contratosAssinados: 0,
                    contratosAtivados: item.linhasAtivadas,
                    valorMensal: item.valorMensal
                  }))}
                  title={`Evolução Temporal de Linhas - ${getPeriodSubtitle()}`}
                  subtitle="Linhas criadas e ativadas ao longo do tempo"
                />
              </Grid.Column>
            </Grid>
          </div>
        )}

        {/* Gráficos */}
        <Grid stackable columns={2} style={{ marginTop: GlassmorphismTheme.spacing.lg }}>
          <Grid.Column>
            <StatusPieChart
              data={pieChartData}
              title="Distribuição de Status"
              subtitle={`Total: ${stats?.contractStats.totalContratos || 0} contratos`}
            />
          </Grid.Column>
          <Grid.Column>
            <ConversionFunnelChart
              data={funnelData}
              title="Funil de Conversão"
            />
          </Grid.Column>
        </Grid>

        <Grid stackable columns={1} style={{ marginTop: GlassmorphismTheme.spacing.md }}>
          <Grid.Column>
            <PeriodLineChart
              data={periodChart}
              title={`Evolução Temporal - ${getPeriodSubtitle()}`}
              subtitle="Criados, Assinados e Ativados ao longo do tempo"
            />
          </Grid.Column>
        </Grid>

        {performanceData.length > 0 && (
          <Grid stackable columns={1} style={{ marginTop: GlassmorphismTheme.spacing.md }}>
            <Grid.Column>
              <PerformanceBarChart
                data={performanceData}
                title={isSeller ? 'Sua Performance' : isManager ? 'Performance da Equipe' : 'Performance por Time'}
                subtitle={isSeller ? 'Seus contratos em destaque' : 'Comparação entre vendedores/times'}
                showValue={!isSeller}
              />
            </Grid.Column>
          </Grid>
        )}


        {/* Gráficos Consolidados - Visível para Admin, Manager, BKO e Validator */}
        {!isSeller && (
          <>
            <Grid stackable columns={2} style={{ marginTop: GlassmorphismTheme.spacing.lg }}>
              <Grid.Column>
                <OperationalChart
                  data={operationalChart}
                  title="Gráfico Operacional"
                  subtitle={`Criado Easy Vendas vs Input Easy Vendas - ${getPeriodSubtitle()}`}
                />
              </Grid.Column>
              <Grid.Column>
                <FinancialChart
                  data={financialChart}
                  title="Gráfico Financeiro"
                  subtitle={`Receita de Aditivos vs Renegociações - ${getPeriodSubtitle()}`}
                />
              </Grid.Column>
            </Grid>

          </>
        )}

        </div>
      </Dimmer.Dimmable>

      {/* Modal de listagem de contratos por status */}
      <ContractListModal
        isOpen={showContractModal}
        onClose={handleCloseModal}
        statuses={selectedStatuses}
        statusLabel={selectedStatusLabel}
        mode={modalMode}
        teamId={selectedTeamId}
        periodKey={useCustomPeriod ? 'personalizado' : selectedPeriod}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
        getPeriodDates={getPeriodDates}
      />
    </GlassPageWrapper>
  );
};

export default DashboardPage;
